import { useCallback, useEffect, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  PhoneOff,
  ScreenShare,
  ScreenShareOff,
  MessageSquare,
  MessageSquareOff,
  Users,
  Settings,
  AlertTriangle,
} from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { useVisioRoom } from '../video/use-visio-room';
import type { VisioRole } from '../video/video-service';
import {
  useLiveTranscription,
  transcriptionErrorGuidance,
} from '../transcription/use-live-transcription';
import { SupabaseMessagingService } from '../chat/messaging-service';
import { ParticipantGrid } from './participant-grid';
import { ParticipantsPanel } from './participants-panel';
import { PrejoinScreen } from './prejoin-screen';
import { ChatPanel } from './chat-panel';
import { SeriesChecklist } from '../series/series-checklist';
import { useWaitingParticipants } from '../participants/use-waiting-participants';
import { useParticipantNames, withServerNames } from '../participants/use-participant-names';
import { RoomSettingsPanel } from '../settings/room-settings-panel';

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-4 py-6 text-center sm:py-8">
      {children}
    </div>
  );
}

/** Bouton icône de la barre d'actions fixe — ≥44×44px (mobile-first), label court en dessous. */
function BarButton({
  icon,
  label,
  onClick,
  active = false,
  destructive = false,
  loading = false,
  badge = false,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  destructive?: boolean;
  loading?: boolean;
  badge?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      aria-label={label}
      className={cn(
        'relative flex min-h-[2.75rem] min-w-[2.75rem] flex-1 flex-col items-center justify-center gap-0.5 rounded-xl py-1.5 transition-colors active:scale-95 disabled:opacity-50',
        destructive
          ? 'bg-accent-500 hover:bg-accent-600 text-white'
          : active
            ? 'bg-primary-100 text-primary-700'
            : 'text-ink-600 hover:bg-ink-100',
      )}
    >
      {icon}
      <span className="text-[10px] font-semibold leading-none">{label}</span>
      {badge && (
        <span
          className="bg-accent-500 absolute right-1.5 top-1 h-2 w-2 rounded-full"
          aria-hidden="true"
        />
      )}
    </button>
  );
}

/**
 * Choix du TUTEUR en quittant la visio :
 * - « Quitter, je reviens » : il sort, la salle reste ouverte (les autres l'attendent,
 *   il peut la rejoindre à nouveau) ;
 * - « Terminer la réunion » : fin RÉELLE pour tout le monde — l'heure de fin devient
 *   maintenant et le rendez-vous passe en « rendez-vous passé ».
 * Le geste destructif est un choix explicite et séparé (jamais le bouton par défaut).
 */
function LeaveSheet({
  busy,
  error,
  onLeave,
  onEnd,
  onCancel,
}: {
  busy: boolean;
  error: string | null;
  onLeave: () => void;
  onEnd: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center bg-black/50 sm:items-center"
      onClick={() => {
        if (!busy) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="leave-sheet-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md space-y-2 rounded-t-2xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:rounded-2xl"
      >
        <p id="leave-sheet-title" className="text-ink-900 text-base font-bold">
          Quitter la visio ?
        </p>

        <button
          type="button"
          onClick={onLeave}
          disabled={busy}
          className="border-primary-200 bg-primary-50 text-primary-800 flex min-h-[2.75rem] w-full flex-col items-start justify-center rounded-xl border px-4 py-2 text-left transition-colors active:scale-[0.98] disabled:opacity-50"
        >
          <span className="text-sm font-semibold">Quitter, je reviens</span>
          <span className="text-xs opacity-80">
            La salle reste ouverte : tu pourras la rejoindre à nouveau.
          </span>
        </button>

        <button
          type="button"
          onClick={onEnd}
          disabled={busy}
          className="border-accent-200 bg-accent-50 text-accent-800 flex min-h-[2.75rem] w-full flex-col items-start justify-center rounded-xl border px-4 py-2 text-left transition-colors active:scale-[0.98] disabled:opacity-50"
        >
          <span className="text-sm font-semibold">Terminer la réunion</span>
          <span className="text-xs opacity-80">
            Fin pour tout le monde : l’heure de fin devient maintenant et le rendez-vous passe en «
            rendez-vous passé ».
          </span>
        </button>

        {error && (
          <p role="alert" className="text-accent-700 text-xs font-medium">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-ink-600 min-h-[2.75rem] w-full rounded-xl text-sm font-semibold active:scale-[0.98] disabled:opacity-50"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}

/**
 * Écran de salle vidéo — partagé entre `apps/tutor` (tutor.jeraisonne.fr,
 * accès web autonome) et la route native `/rendez-vous/$id/room` d'`apps/app`
 * (même origine, pas de handoff SSO). `db`/`supabaseUrl` et `onExit` sont
 * INJECTÉS : chaque app a son propre client Supabase et sa propre notion de
 * "quitter" (navigation externe vers app.jeraisonne.fr pour `apps/tutor`,
 * navigation interne SPA pour `apps/app`).
 *
 * De 1:1 à N participants (salle à plusieurs, migrations 0255-0257 + moteur multi-pairs) :
 * `me.role === 'tutor'` reste, pour cette phase (comptes connectés uniquement, pas
 * d'invité anonyme), la seule notion d'HÔTE — cohérente avec le repli `hostUserId` absent de
 * `WebRtcVideoService` (documenté dans CLAUDE.md). Un futur invité (Phase D) exigera de
 * transmettre le VRAI `hostUserId` serveur ici.
 */
export function RoomPage({
  roomId,
  me,
  db,
  supabaseUrl,
  onExit,
  exitLabel = 'Retour à JeRaisonne',
  buildInviteUrl,
}: {
  roomId: string;
  me: { userId: string; role: VisioRole; displayName: string };
  db: SupabaseClient;
  supabaseUrl: string;
  onExit: () => void;
  exitLabel?: string;
  /**
   * Construit le lien d'invitation partageable de CETTE salle (Phase D,
   * migration 0259) — INJECTÉ car l'origine diffère selon l'app appelante
   * (`apps/app` construit vers `tutor.jeraisonne.fr`, `apps/tutor` vers sa
   * propre origine). Omis = aucun bouton « Réglages » (pas de régression sur
   * un appelant qui ne veut pas exposer cette fonctionnalité).
   */
  buildInviteUrl?: (token: string) => string;
}) {
  const isHost = me.role === 'tutor';
  const room = useVisioRoom({ roomId, me, db, supabaseUrl, prejoin: true });
  // Noms vus par MOI (vrai nom pour le prof, pseudo pour les autres élèves).
  const serverNames = useParticipantNames(
    db,
    roomId,
    room.participants.map((p) => p.userId),
  );
  const shownParticipants = withServerNames(room.participants, serverNames);
  const waitingRoom = useWaitingParticipants(db, roomId, isHost);
  const [leaving, setLeaving] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);
  const [showChat, setShowChat] = useState(true);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const onUtterance = useCallback(
    (text: string) => {
      const messagingService = new SupabaseMessagingService(db);
      messagingService.send(roomId, text, 'voice').catch((err: unknown) => {
        console.error('[transcription] envoi au chat échoué', err);
      });
    },
    [roomId, db],
  );
  const transcription = useLiveTranscription(room.localStream, onUtterance);

  // Côté non-hôte, seul l'hôte DÉCIDE d'activer la retranscription
  // (room.sendTranscriptionState) — mais DÉMARRER le moteur de reconnaissance
  // vocale exige un geste utilisateur DIRECT sur CET appareil : un démarrage
  // déclenché depuis un effet réagissant à un signal distant, sans tap local,
  // échoue systématiquement avec `service-not-allowed` sur Safari (observé en
  // test réel — ça fonctionnait quand chaque participant avait son propre
  // bouton "Texte", donc un geste direct). L'ARRÊT, lui, n'a besoin d'aucun
  // geste — coupé automatiquement dès que l'hôte désactive. cf. bandeau
  // "Ton prof a activé la retranscription" plus bas pour le geste de démarrage.
  const { stop: stopTranscription } = transcription;
  useEffect(() => {
    if (isHost) return;
    if (!room.remoteTranscriptionActive) stopTranscription();
  }, [isHost, room.remoteTranscriptionActive, stopTranscription]);

  if (room.phase === 'prejoin') {
    return <PrejoinScreen onJoin={room.startJoin} />;
  }

  if (room.phase === 'requesting') {
    return (
      <CenteredMessage>
        <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        <p className="text-ink-500 text-sm">Demande d'accès à la caméra et au micro…</p>
      </CenteredMessage>
    );
  }

  if (room.phase === 'denied') {
    return (
      <CenteredMessage>
        <p className="text-ink-900 text-lg font-semibold">Caméra/micro indisponibles</p>
        <p className="text-ink-500 max-w-sm text-sm">
          Autorise l'accès à la caméra et au micro dans les réglages de ton navigateur pour une
          expérience complète, ou continue en simple visioconférence.
        </p>
        <Button onClick={room.continueWithoutMedia} className="min-h-[2.75rem] w-full max-w-xs">
          Continuer sans caméra/micro
        </Button>
      </CenteredMessage>
    );
  }

  if (room.phase === 'joining') {
    return (
      <CenteredMessage>
        <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        <p className="text-ink-500 text-sm">Connexion à la salle…</p>
      </CenteredMessage>
    );
  }

  if (room.excludedByHost) {
    return (
      <CenteredMessage>
        <p className="text-ink-900 text-lg font-semibold">Tu as été exclu de cette salle</p>
        <p className="text-ink-500 max-w-sm text-sm">
          L'hôte t'a retiré de la réunion. La séance continue pour les autres participants.
        </p>
        <Button onClick={onExit} className="min-h-[2.75rem] w-full max-w-xs">
          {exitLabel}
        </Button>
      </CenteredMessage>
    );
  }

  if (room.endedByPeer) {
    return (
      <CenteredMessage>
        <p className="text-ink-900 text-lg font-semibold">Séance terminée</p>
        <p className="text-ink-500 max-w-sm text-sm">
          {isHost ? 'La séance' : 'Ton professeur'} a mis fin à la séance.
        </p>
        <Button onClick={onExit} className="min-h-[2.75rem] w-full max-w-xs">
          {exitLabel}
        </Button>
      </CenteredMessage>
    );
  }

  if (room.connectionState === 'failed') {
    return (
      <CenteredMessage>
        <p className="text-ink-900 text-lg font-semibold">Connexion impossible</p>
        <p className="text-ink-500 max-w-sm text-sm">
          La connexion vidéo n'a pas pu s'établir. Vérifie ta connexion internet et réessaie.
        </p>
        <Button
          onClick={() => window.location.reload()}
          className="min-h-[2.75rem] w-full max-w-xs"
        >
          Réessayer
        </Button>
      </CenteredMessage>
    );
  }

  const waitingForAnyone = room.participants.length === 0;

  function handleToggleTranscription() {
    const next = !transcription.active;
    if (next) transcription.start();
    else transcription.stop();
    room.sendTranscriptionState(next);
  }

  /** Sortir SANS terminer : la salle reste ouverte (l'hôte « revient », les autres attendent). */
  async function handleLeave() {
    setLeaving(true);
    try {
      await room.leave();
      onExit();
    } finally {
      setLeaving(false);
    }
  }

  /** Terminer RÉELLEMENT (hôte) : si la fin n'a pas pu être enregistrée on reste dans la salle et on le dit. */
  async function handleEnd() {
    setLeaving(true);
    setEndError(null);
    try {
      await room.end();
      onExit();
    } catch {
      setEndError('La réunion n’a pas pu être terminée. Vérifie ta connexion et réessaie.');
    } finally {
      setLeaving(false);
    }
  }

  /** L'hôte exclut : écriture serveur (empêche un retour) PUIS signal (fait sortir tout de suite). */
  function handleExcluded(userId: string) {
    room.removeParticipant(userId);
  }

  return (
    // `--visio-bar-h` = hauteur EXACTE de la barre d'actions (elle a une
    // hauteur explicite) : le chat s'y accroche (saisie fixée juste au-dessus
    // sur mobile, colonne de droite arrêtée au-dessus sur desktop) sans nombre
    // magique dupliqué. Pas d'`overflow` sur le conteneur de contenu : il
    // casserait le `sticky` de la colonne chat.
    <div className="flex min-h-dvh flex-col [--visio-bar-h:calc(4rem_+_env(safe-area-inset-bottom))]">
      <div
        className={cn(
          'flex-1 px-4 py-6 sm:py-8',
          // mobile : place pour la saisie fixée (≈ 4 rem) quand le chat est affiché.
          showChat
            ? 'pb-[calc(var(--visio-bar-h)_+_5.5rem)] lg:pb-[calc(var(--visio-bar-h)_+_1rem)]'
            : 'pb-[calc(var(--visio-bar-h)_+_1rem)]',
        )}
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-4 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1 space-y-4">
            {waitingForAnyone && (
              <p className="text-ink-500 text-center text-sm">
                En attente des autres participants…
              </p>
            )}
            {!waitingForAnyone && room.connectionState === 'reconnecting' && (
              <p className="text-center text-sm font-medium text-amber-600">
                Reconnexion en cours…
              </p>
            )}

            <ParticipantGrid
              local={{
                stream: room.localStream,
                micOn: room.micEnabled,
                cameraOn: room.cameraEnabled,
                screenSharing: room.screenSharing,
              }}
              localIsHost={isHost}
              localLabel="Toi"
              participants={shownParticipants}
            />

            {/* Outil de l'hôte : points à aborder (jamais montré aux autres). */}
            {isHost && <SeriesChecklist db={db} roomId={roomId} />}

            {!isHost &&
              transcription.supported &&
              room.remoteTranscriptionActive &&
              !transcription.active &&
              !transcription.error && (
                <div className="border-primary-200 bg-primary-50 text-primary-700 flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-xs font-medium">
                  <div className="flex items-center gap-2">
                    <Mic className="size-3.5 shrink-0" />
                    <span>Ton prof a activé la retranscription en direct.</span>
                  </div>
                  <button
                    type="button"
                    onClick={transcription.start}
                    className="border-primary-300 text-primary-700 min-h-[2.75rem] shrink-0 rounded-lg border bg-white px-3 text-xs font-semibold transition-colors active:scale-95"
                  >
                    Activer
                  </button>
                </div>
              )}

            {transcription.supported && (transcription.active || transcription.error) && (
              <div
                className={cn(
                  'flex flex-col gap-2 rounded-xl border px-3 py-2 text-xs font-medium',
                  transcription.error
                    ? 'border-accent-200 bg-accent-50 text-accent-700'
                    : 'border-primary-200 bg-primary-50 text-primary-700',
                )}
              >
                <div className="flex items-center gap-2">
                  {transcription.error ? (
                    <AlertTriangle className="size-3.5 shrink-0" />
                  ) : (
                    <Mic className="size-3.5 shrink-0" />
                  )}
                  <span>
                    {transcription.error
                      ? `Retranscription indisponible (${transcription.error}) — ${transcriptionErrorGuidance(transcription.error)}`
                      : transcription.listening
                        ? 'Retranscription en direct active — postée dans le chat au fil de l’eau.'
                        : 'Retranscription : démarrage du moteur de reconnaissance…'}
                  </span>
                </div>
                {!transcription.error && transcription.interimText && (
                  <p className="italic opacity-80">« {transcription.interimText} »</p>
                )}
                {transcription.error && (
                  <button
                    type="button"
                    onClick={transcription.start}
                    className="border-accent-300 text-accent-700 min-h-[2.75rem] self-start rounded-lg border bg-white px-3 text-xs font-semibold transition-colors active:scale-95"
                  >
                    Réessayer
                  </button>
                )}
              </div>
            )}
          </div>

          {showChat && (
            // desktop : colonne de droite collée en haut, arrêtée juste au-dessus
            // de la barre → la saisie est en bas à droite de l'écran.
            <div className="lg:sticky lg:top-8 lg:h-[calc(100dvh_-_var(--visio-bar-h)_-_3rem)] lg:w-96 lg:shrink-0">
              <ChatPanel roomId={roomId} myUserId={me.userId} myRole={me.role} db={db} />
            </div>
          )}
        </div>
      </div>

      <div className="border-ink-100 fixed inset-x-0 bottom-0 z-10 box-border h-[var(--visio-bar-h)] border-t bg-white/95 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-2 backdrop-blur">
        <div className="mx-auto flex max-w-lg gap-1 px-3">
          <BarButton
            icon={room.micEnabled ? <Mic className="size-5" /> : <MicOff className="size-5" />}
            label="Micro"
            active={room.micEnabled}
            onClick={room.toggleMic}
          />
          <BarButton
            icon={
              room.cameraEnabled ? <Video className="size-5" /> : <VideoOff className="size-5" />
            }
            label="Caméra"
            active={room.cameraEnabled}
            onClick={room.toggleCamera}
          />
          <BarButton
            icon={
              room.screenSharing ? (
                <ScreenShareOff className="size-5" />
              ) : (
                <ScreenShare className="size-5" />
              )
            }
            label="Écran"
            active={room.screenSharing}
            onClick={() => void room.toggleScreenShare()}
          />
          <BarButton
            icon={<Users className="size-5" />}
            label={`Participants${room.participants.length > 0 ? ` (${room.participants.length + 1})` : ''}`}
            active={showParticipants}
            badge={isHost && waitingRoom.waiting.length > 0}
            onClick={() => setShowParticipants(true)}
          />
          <BarButton
            icon={
              showChat ? (
                <MessageSquare className="size-5" />
              ) : (
                <MessageSquareOff className="size-5" />
              )
            }
            label="Chat"
            active={showChat}
            onClick={() => setShowChat((v) => !v)}
          />
          {isHost && transcription.supported && (
            <BarButton
              icon={<Mic className="size-5" />}
              label="Texte"
              active={transcription.active}
              badge={!!transcription.error}
              onClick={handleToggleTranscription}
            />
          )}
          {isHost && buildInviteUrl && (
            <BarButton
              icon={<Settings className="size-5" />}
              label="Réglages"
              active={showSettings}
              onClick={() => setShowSettings(true)}
            />
          )}
          <BarButton
            icon={<PhoneOff className="size-5" />}
            label="Quitter"
            destructive
            loading={leaving}
            // Non-hôte : sortie directe. Hôte : choix « je reviens » / « terminer ».
            onClick={isHost ? () => setLeaveOpen(true) : () => void handleLeave()}
          />
        </div>
      </div>

      {leaveOpen && isHost && (
        <LeaveSheet
          busy={leaving}
          error={endError}
          onLeave={() => void handleLeave()}
          onEnd={() => void handleEnd()}
          onCancel={() => {
            setLeaveOpen(false);
            setEndError(null);
          }}
        />
      )}

      {showParticipants && (
        <ParticipantsPanel
          participants={shownParticipants}
          myUserId={me.userId}
          myDisplayName={me.displayName}
          myMicOn={room.micEnabled}
          myCameraOn={room.cameraEnabled}
          isHost={isHost}
          roomId={roomId}
          db={db}
          waiting={waitingRoom.waiting}
          onClose={() => setShowParticipants(false)}
          onRequestMute={room.requestMute}
          onExcluded={handleExcluded}
          onAdmit={waitingRoom.admit}
          onDeny={waitingRoom.deny}
        />
      )}

      {showSettings && buildInviteUrl && (
        <RoomSettingsPanel
          roomId={roomId}
          db={db}
          buildInviteUrl={buildInviteUrl}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}
